import { useMemo, useState } from 'react';

import {
	SUPPORTER_VOICES_FEED_DEFAULT_ITEMS_PER_ROW as DEFAULT_ITEMS_PER_ROW,
	usePetitionDetailsPageData,
} from '#src/app/pages/petitionDetails/pageContext';
import type { SupporterVoicesFeedItem } from '#src/app/pages/petitionDetails/pageContext';
import { useCampaignActionsEnabled } from '#src/app/pages/petitionDetails/shared/hooks';
import { useCanPlayWebm } from '#src/app/shared/hooks/media';

import type { SupporterCommentPetition } from '../../SupporterVoicesComments/shared/types';

// When the FCM is on, show the mixed feed for any petition with at least one supporter voice
// (comment or video) — including comments-only petitions. Only fall back to the legacy layout
// when there's nothing to render.
const MIN_ITEMS_TO_SHOW = 1;

type CommentFeedItem = Extract<SupporterVoicesFeedItem, { __typename: 'PetitionSupporterComment' }>;
type VideoFeedItem = Extract<SupporterVoicesFeedItem, { __typename: 'PetitionSupporterVideo' }>;

type FeedItem = { type: 'comment'; data: CommentFeedItem } | { type: 'video'; data: VideoFeedItem };

type Result = ModelHookResult<
	{
		feedItems: readonly FeedItem[];
		petition: SupporterCommentPetition;
		currentlyPlaying: string;
		showFeed: boolean;
		showCreateVideo: boolean;
		itemsPerRow: number;
	},
	{
		setCurrentlyPlaying: (videoUrl: string) => void;
		removeVideoFromState: (videoId?: string) => void;
	}
>;

/**
 * Right now GQL returns all transcoded + unprocessed videos. We want to display transcoded videos
 * for browsers (e.g., iOS) that don't support .webm. Mux URLs are always included because
 * MuxPlayer handles format selection internally.
 */
function canDisplayVideo(videoUrl: string, canPlayWebm: boolean): boolean {
	return canPlayWebm || videoUrl.endsWith('.mp4') || /^https?:\/\/stream\.mux\.com\//.test(videoUrl);
}

function toFeedItems(feed: readonly SupporterVoicesFeedItem[], canPlayWebm: boolean): FeedItem[] {
	return feed.flatMap((item): FeedItem[] => {
		if (item.__typename === 'PetitionSupporterComment') {
			return [{ type: 'comment', data: item }];
		}
		if (!canDisplayVideo(item.videoUrl, canPlayWebm)) return [];
		return [{ type: 'video', data: item }];
	});
}

export function useSupporterVoicesFeed(): Result {
	const {
		data: {
			petition,
			supporterVoicesFeed,
			featureConfigs: { supporterVoicesMix },
		},
	} = usePetitionDetailsPageData();

	const itemsPerRow = supporterVoicesMix?.itemsPerRow ?? DEFAULT_ITEMS_PER_ROW;

	const campaignActionsEnabled = useCampaignActionsEnabled();
	const showCreateVideo = campaignActionsEnabled && petition.canCreateVideo;

	const canPlayWebm = useCanPlayWebm();

	const [currentlyPlaying, setCurrentlyPlaying] = useState('');

	// Only the initial value matters here, mirroring the previous useSupporterVideos state, which
	// never resynced with a later canPlayWebm change either.
	// oxlint-disable-next-line react-hooks/exhaustive-deps
	const initialFeedItems = useMemo(() => toFeedItems(supporterVoicesFeed, canPlayWebm), []);

	const [feedItems, setFeedItems] = useState<readonly FeedItem[]>(initialFeedItems);

	const removeVideoFromState = (videoId?: string) => {
		setFeedItems((current) => current.filter((item) => !(item.type === 'video' && item.data.id === videoId)));
	};

	return {
		data: {
			feedItems,
			petition,
			currentlyPlaying,
			showFeed: feedItems.length >= MIN_ITEMS_TO_SHOW,
			showCreateVideo,
			itemsPerRow,
		},
		actions: {
			setCurrentlyPlaying,
			removeVideoFromState,
		},
	};
}
